import { useMemo, useState } from "react";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";

import { formatCurrency, formatPercent } from "../../../../../utils";
import { buildBorrowingCost, buildDebtMix } from "./insights";
import {
  Body,
  Chevron,
  CollapseLabel,
  Column,
  ColumnHeader,
  ColumnSubtitle,
  ColumnTitle,
  DebtMixFill,
  Header,
  HeaderTitle,
  InterestSegment,
  Legend,
  LegendItem,
  LegendSwatch,
  Name,
  Panel,
  PrincipalSegment,
  Row,
  RowList,
  SegmentReveal,
  Swatch,
  Track,
  Value,
} from "./styles";
import { LiabilitiesInsightsProps } from "./types";

export const LiabilitiesInsights: React.FC<LiabilitiesInsightsProps> = ({
  liabilities,
  isCompact,
}) => {
  const [expanded, setExpanded] = useState(!isCompact);

  const debtMix = useMemo(() => buildDebtMix(liabilities), [liabilities]);
  const borrowingCost = useMemo(
    () => buildBorrowingCost(liabilities),
    [liabilities],
  );

  const toggleExpanded = () => {
    setExpanded((current) => !current);
  };

  const collapseLabel = expanded ? "Collapse" : "Expand";

  return (
    <Panel>
      <Header
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-label="Toggle insights section"
      >
        <Chevron $expanded={expanded}>
          <ExpandLessRoundedIcon />
        </Chevron>
        <HeaderTitle>Insights</HeaderTitle>
        <CollapseLabel>{collapseLabel}</CollapseLabel>
      </Header>

      {expanded && (
        <Body>
          <Column>
            <ColumnHeader>
              <ColumnTitle>Debt mix</ColumnTitle>
              <ColumnSubtitle>By liability</ColumnSubtitle>
            </ColumnHeader>
            <RowList>
              {debtMix.map((item) => (
                <Row key={item.id}>
                  <Name>
                    <Swatch $color={item.color} />
                    <span>{item.name}</span>
                  </Name>
                  <Track>
                    <DebtMixFill $width={item.percent} $color={item.color} />
                  </Track>
                  <Value>{formatPercent(item.percent)}</Value>
                </Row>
              ))}
            </RowList>
          </Column>

          <Column>
            <ColumnHeader>
              <ColumnTitle>Cost of borrowing</ColumnTitle>
              <ColumnSubtitle>Principal vs interest paid</ColumnSubtitle>
            </ColumnHeader>
            <RowList>
              {borrowingCost.map((item) => (
                <Row key={item.id}>
                  <Name>
                    <span>{item.name}</span>
                  </Name>
                  <Track>
                    <SegmentReveal>
                      <PrincipalSegment $width={item.principalWidth} />
                      <InterestSegment $width={item.interestWidth} />
                    </SegmentReveal>
                  </Track>
                  <Value>{formatCurrency(item.interest)}</Value>
                </Row>
              ))}
            </RowList>
            <Legend>
              <LegendItem>
                <LegendSwatch $tone="principal" />
                Principal
              </LegendItem>
              <LegendItem>
                <LegendSwatch $tone="interest" />
                Interest
              </LegendItem>
            </Legend>
          </Column>
        </Body>
      )}
    </Panel>
  );
};
